import {normalizeIdCountry} from '@sollapay/domain';
import {PartyType} from '@sollapay/enums';

import type {CreateBuyerFormValues} from '@/features/buyers/validation';
import type {PartyRole} from '@sollapay/enums';
import type {BeneficiaryDTO, FatcaCrsBeneficiarySnapshotDTO} from '@sollapay/types';

/**
 * Maps the live beneficiary roster (from the API) into snapshot rows for the party table.
 * Used to populate the existing-beneficiaries section of the supplementary FATCA/CRS form.
 */
export const toBeneficiarySnapshots = (
  beneficiaries: BeneficiaryDTO[]
): FatcaCrsBeneficiarySnapshotDTO[] =>
  beneficiaries.map(b => ({
    name: b.fullName,
    role: b.role,
    idOrCompanyNumber: b.identifier,
    idType: b.idType,
    idCountry: b.idCountry ?? null,
    birthOrIncorporationDate: b.birthOrIncorporationDate || null,
    address: b.address || null
  }));

/** Added-beneficiaries snapshot rows, one per buyer. Every row shares the same `role` — the trust type's party-role strategy, not person-vs-company — matching the backend's buildAddedBuyersSnapshot. */
export const toBuyerBeneficiarySnapshots = (
  values: CreateBuyerFormValues,
  role: PartyRole
): FatcaCrsBeneficiarySnapshotDTO[] =>
  values.buyers
    .map(entry =>
      entry.partyType === PartyType.ORGANIZATION
        ? {
            name: entry.organization.companyName,
            role,
            idOrCompanyNumber: entry.organization.incorporationNumber,
            birthOrIncorporationDate: entry.organization.incorporationDate || null,
            address: entry.organization.address || null,
            partyType: entry.partyType
          }
        : {
            name: entry.person.fullName,
            role,
            idOrCompanyNumber: entry.person.nationalId,
            idType: entry.person.idType,
            idCountry: normalizeIdCountry(entry.person.idType, entry.person.idCountry),
            birthOrIncorporationDate: entry.person.dateOfBirth || null,
            address: entry.person.address || null,
            partyType: entry.partyType
          }
    )
    .filter(snapshot => snapshot.name.trim().length > 0);
